import React, { useState, useEffect, useRef } from 'react';
import { Box, Paper, Typography, Divider, CircularProgress, IconButton, Tooltip, Stack, Fade, Chip } from '@mui/material';
import ReactAudioPlayer from 'react-audio-player';
import ShareIcon from '@mui/icons-material/Share';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { TherapistResponse } from '../types/types';

interface VerseDisplayProps {
  response: TherapistResponse;
}

const VerseDisplay: React.FC<VerseDisplayProps> = ({ response }) => {
  const { verse, reflection } = response;
  const [audioUrl, setAudioUrl] = useState<string | undefined>(undefined);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioError, setAudioError] = useState<string | null>(null);
  const audioRef = useRef<any>(null); // ReactAudioPlayer ref
  const nativeAudioRef = useRef<HTMLAudioElement | null>(null); // Native audio ref for iOS
  const [isIOS, setIsIOS] = useState(false);

  // Format the share text with proper spacing and emojis
  const shareText = `🌟 القرآن المرشد 🌟\n\n` +
    `📖 ${verse.arabic}\n\n` +
    `🌿 ${verse.english}\n\n` +
    `📍 ${verse.reference}\n\n` +
    `💭 تأمل وتدبر:\n${reflection.arabic}\n\n` +
    `#القرآن_المرشد #آيات_قرآنية #سكينة_وطمأنينة`;

  useEffect(() => {
    // Detect iOS device
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(isIOSDevice);
  }, []);

  useEffect(() => {
    // Reset states when verse changes
    setAudioUrl(undefined);
    setIsLoadingAudio(false);
    setAudioError(null);
    setIsPlaying(false);

    if (verse.audioUrl) {
      setAudioUrl(verse.audioUrl);
      setIsLoadingAudio(true); // Start loading state
    }
  }, [verse.audioUrl]);

  const handleAudioError = () => {
    if (verse.fallbackAudioUrl && audioUrl !== verse.fallbackAudioUrl) {
      setAudioUrl(verse.fallbackAudioUrl);
      setIsLoadingAudio(true);
    } else {
      setAudioError('عذراً، التلاوة غير متاحة حالياً');
      setIsLoadingAudio(false);
    }
  };

  const handleAudioLoadStart = () => {
    setIsLoadingAudio(true);
    setAudioError(null);
  };

  const handleAudioCanPlay = () => {
    setIsLoadingAudio(false);
    setAudioError(null);
  };

  const togglePlay = () => {
    if (isIOS) {
      if (nativeAudioRef.current) {
        if (isPlaying) {
          nativeAudioRef.current.pause();
        } else {
          nativeAudioRef.current.play();
        }
        setIsPlaying(!isPlaying);
      }
    } else {
      if (audioRef.current && audioRef.current.audioEl.current) {
        if (isPlaying) {
          audioRef.current.audioEl.current.pause();
        } else {
          audioRef.current.audioEl.current.play();
        }
        setIsPlaying(!isPlaying);
      }
    }
  };

  return (
    <Fade in={true} timeout={800}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 5 },
          mt: 4,
          borderRadius: 6,
          background: '#FFFFFF',
          boxShadow: '0 20px 60px -10px rgba(15, 76, 58, 0.1)',
          border: '1px solid rgba(15, 76, 58, 0.08)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Decorative Top Border */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '6px',
          background: (theme) => `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
        }} />

        <Box dir="rtl">
          {/* Verse Section */}
          <Box sx={{ mb: 5, position: 'relative' }}>
            <FormatQuoteIcon sx={{
              fontSize: 60,
              color: 'primary.light',
              opacity: 0.1,
              position: 'absolute',
              top: -20,
              right: -10
            }} />

            <Typography
              variant="h3"
              align="center"
              sx={{
                fontFamily: '"Amiri", "Noto Naskh Arabic", serif',
                fontSize: { xs: '1.8rem', md: '2.4rem' },
                fontWeight: 700,
                color: 'primary.dark',
                lineHeight: 2.2,
                mb: 3,
                px: 2,
                position: 'relative',
              }}
            >
              {verse.arabic}
            </Typography>

            <Typography
              variant="subtitle1"
              align="center"
              sx={{
                color: 'text.secondary',
                fontSize: '1.1rem',
                fontFamily: '"Inter", sans-serif',
                mb: 2,
                direction: 'ltr'
              }}
            >
              {verse.english}
            </Typography>

            <Box display="flex" justifyContent="center" mt={2}>
              <Chip
                label={verse.reference}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  fontFamily: '"Amiri", serif',
                  fontSize: '1rem',
                  fontWeight: 600,
                  px: 2,
                  py: 2.5,
                  borderRadius: 50
                }}
              />
            </Box>
          </Box>

          <Divider sx={{ my: 4, borderColor: 'rgba(0,0,0,0.06)' }} />

          {/* Reflection Section */}
          <Box sx={{ mb: 5 }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <AutoAwesomeIcon color="secondary" />
              <Typography
                variant="h5"
                sx={{
                  fontFamily: '"Amiri", serif',
                  color: 'primary.main',
                  fontWeight: 700
                }}
              >
                التأمل والتدبر
              </Typography>
            </Box>

            <Typography
              variant="body1"
              sx={{
                fontFamily: '"Amiri", serif',
                fontSize: { xs: '1.3rem', md: '1.5rem' },
                lineHeight: 2,
                color: 'text.primary',
                textAlign: 'justify',
                mb: 3,
                backgroundColor: 'rgba(212, 175, 55, 0.05)',
                p: 3,
                borderRadius: 4,
                borderRight: '4px solid #D4AF37'
              }}
            >
              {reflection.arabic}
            </Typography>

            {reflection.english && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  direction: 'ltr',
                  textAlign: 'left',
                  fontSize: '1rem',
                  lineHeight: 1.7,
                  opacity: 0.8,
                  pl: 2,
                  borderLeft: '2px solid rgba(0,0,0,0.1)'
                }}
              >
                {reflection.english}
              </Typography>
            )}

          </Box>

          {/* Audio & Actions Section */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 4,
              backgroundColor: '#F8F9FA',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              flexWrap: 'wrap'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
              <IconButton
                onClick={togglePlay}
                disabled={isLoadingAudio || !!audioError}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  width: 50,
                  height: 50,
                  '&:hover': { bgcolor: 'primary.dark' },
                  '&.Mui-disabled': { bgcolor: 'action.disabledBackground' }
                }}
              >
                {isLoadingAudio ? (
                  <CircularProgress size={24} color="inherit" />
                ) : isPlaying ? (
                  <PauseIcon />
                ) : (
                  <PlayArrowIcon />
                )}
              </IconButton>

              <Box>
                <Typography variant="subtitle2" sx={{ fontFamily: '"Amiri", serif', fontWeight: 700 }}>
                  تلاوة الآية
                </Typography>
                {audioError ? (
                  <Typography variant="caption" color="error">{audioError}</Typography>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    استمع بخشوع
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Hidden Players */}
            {isIOS ? (
              <audio
                ref={nativeAudioRef}
                src={audioUrl || ''}
                onError={handleAudioError}
                onLoadStart={handleAudioLoadStart}
                onCanPlay={handleAudioCanPlay}
                onEnded={() => setIsPlaying(false)}
                onPause={() => setIsPlaying(false)}
                onPlay={() => setIsPlaying(true)}
                style={{ display: 'none' }}
              />
            ) : (
              <ReactAudioPlayer
                ref={audioRef}
                src={audioUrl || ''}
                onError={(e) => handleAudioError()}
                onCanPlay={handleAudioCanPlay}
                onEnded={() => setIsPlaying(false)}
                onPause={() => setIsPlaying(false)}
                onPlay={() => setIsPlaying(true)}
                style={{ display: 'none' }}
              />
            )}

            <Tooltip title="مشاركة" arrow>
              <IconButton
                onClick={() => {
                  if (navigator.share) {
                    navigator.share({
                      title: 'القرآن المرشد',
                      text: shareText,
                      url: window.location.href
                    }).catch((error) => console.log('Error sharing:', error));
                  }
                }}
                sx={{
                  color: 'primary.main',
                  border: '1px solid rgba(15, 76, 58, 0.2)',
                  '&:hover': { bgcolor: 'rgba(15, 76, 58, 0.05)' }
                }}
              >
                <ShareIcon />
              </IconButton>
            </Tooltip>
          </Paper>

        </Box>
      </Paper>
    </Fade>
  );
};

export default VerseDisplay; 